import { motion } from "framer-motion";
import { Check, ScanLine, Sparkles } from "lucide-react";

const CARDS = [
  { vendor: "Chipotle", amount: "$14.85", cat: "Meals", top: "12%", scan: true },
  { vendor: "Shell Gas", amount: "$52.40", cat: "Car & truck", top: "34%", scan: false },
  { vendor: "Staples", amount: "$28.99", cat: "Office expense", top: "56%", scan: false },
];

export default function AuthShowcase({ badge, quote, checks = [] }) {
  return (
    <div className="hidden lg:block relative bg-[#0A0A0A] overflow-hidden">
      {/* drifting gold aurora */}
      <motion.div
        className="absolute -top-24 -left-24 h-[26rem] w-[26rem] rounded-full bg-[#D4C3A3]/15 blur-3xl"
        animate={{ x: [0, 60, 0], y: [0, 40, 0], scale: [1, 1.15, 1] }}
        transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute top-1/3 -right-24 h-[22rem] w-[22rem] rounded-full bg-[#8a6e3a]/25 blur-3xl"
        animate={{ x: [0, -50, 0], y: [0, 60, 0], scale: [1.1, 1, 1.1] }}
        transition={{ duration: 19, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* subtle dot grid */}
      <div
        className="absolute inset-0 opacity-[0.14]"
        style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(212,195,163,0.35) 1px, transparent 0)", backgroundSize: "34px 34px" }}
      />

      {/* floating glass receipt cards */}
      <div className="absolute inset-0">
        {CARDS.map((c, i) => (
          <motion.div
            key={c.vendor}
            className="absolute left-1/2 w-64 -translate-x-1/2 rounded-2xl bg-white/[0.05] backdrop-blur-xl border border-white/10 p-4 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)] overflow-hidden"
            style={{ top: c.top }}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: [0, -14, 0], rotate: [-4 + i * 3, -1 + i * 3, -4 + i * 3] }}
            transition={{
              opacity: { duration: 0.9, delay: i * 0.35 },
              y: { duration: 6 + i, repeat: Infinity, ease: "easeInOut", delay: i * 0.4 },
              rotate: { duration: 9 + i, repeat: Infinity, ease: "easeInOut" },
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-[#D4C3A3]">
                <Sparkles className="h-3 w-3" /> AI sorted
              </span>
              <span className="text-[10px] font-mono-num text-zinc-500">{c.top === "12%" ? "just now" : ""}</span>
            </div>
            <p className="text-sm font-semibold text-zinc-100">{c.vendor}</p>
            <div className="flex items-center justify-between mt-2">
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/[0.06] border border-white/10 text-zinc-300">{c.cat}</span>
              <span className="text-base font-bold text-[#E5D6B8] font-mono-num">{c.amount}</span>
            </div>
            {c.scan && (
              <motion.div
                className="absolute left-0 right-0 h-10 bg-gradient-to-b from-transparent via-[#D4C3A3]/40 to-transparent"
                initial={{ top: "-20%" }}
                animate={{ top: "120%" }}
                transition={{ duration: 2.2, repeat: Infinity, ease: "linear" }}
              />
            )}
          </motion.div>
        ))}
      </div>

      {/* bottom overlay content */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
      <div className="absolute bottom-12 left-12 right-12">
        {badge && (
          <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-white/[0.06] border border-white/10 text-[#D4C3A3] text-[11px] font-bold uppercase tracking-[0.2em]">
            <ScanLine className="h-3.5 w-3.5" /> {badge}
          </div>
        )}
        <p className="font-display text-2xl sm:text-3xl font-bold leading-tight tracking-tight text-white">
          {quote}
        </p>
        {checks.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-6 text-sm text-zinc-400 font-medium">
            {checks.map((c) => (
              <span key={c} className="flex items-center gap-1.5"><Check className="h-4 w-4 text-[#D4C3A3]" /> {c}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
